import React, { useEffect, useMemo, useRef, useState } from 'react';
import PipelineHealth from './PipelineHealth';
import { dollars, money, reduceMotion } from './Ring';

const STAGES = [['all', 'All'], ['prep', 'Fact Find'], ['submitted', 'Submitted'], ['approved', 'Approved']];
const LABEL = { red: 'Over limit', amber: 'Near limit', blue: 'Waiting', green: 'On track' };
const ORDER = { over: 0, near: 1, ok: 2 };

const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '–');

/* Pipeline tab: Pipeline health + register of every live file, over first, then near, then oldest. */
export default function PipelineTab({ live, onOpenFile, onStage, initialStage, focusSignal }) {
  const [stage, setStage] = useState(initialStage || 'all');
  const [q, setQ] = useState('');
  const reg = useRef(null);
  const files = live?.files || [];
  const counts = useMemo(() => files.reduce((m, f) => { m[f.stage] = (m[f.stage] || 0) + 1; return m; }, {}), [files]);
  const rows = useMemo(() => files
    .filter((f) => (stage === 'all' || f.stage === stage) && (!q || `${f.client} ${f.lender} ${f.owner} ${f.broker}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (ORDER[a.ph_status] ?? 3) - (ORDER[b.ph_status] ?? 3) || (b.bd_in_stage || 0) - (a.bd_in_stage || 0) || (b.amount || 0) - (a.amount || 0)), [files, stage, q]);
  const total = rows.reduce((s, f) => s + (f.amount || 0), 0);

  useEffect(() => {
    if (!focusSignal) return;
    if (initialStage) setStage(initialStage);
    const id = setTimeout(() => reg.current?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' }), 60);
    return () => clearTimeout(id);
  }, [focusSignal, initialStage]);

  return (
    <div className="gpj-grid" data-testid="gpj-pipeline-tab">
      <PipelineHealth live={live} onStage={(k) => (k === 'settled' ? onStage?.(k) : (setStage(k), reg.current?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' })))} />
      <section className="gpj-panel" ref={reg}>
        <div className="gpj-panel-h"><h2>Files in progress</h2><p data-testid="gpj-register-sub">{rows.length} files · {money(total)}</p></div>
        <div className="gpj-chips">
          {STAGES.map(([k, l]) => (
            <button key={k} type="button" className={`gpj-chip${stage === k ? ' on' : ''}`} onClick={() => setStage(k)} data-testid={`gpj-filter-${k}`}>{l}{k !== 'all' ? ` · ${counts[k] || 0}` : ''}</button>
          ))}
          <input className="gpj-search" placeholder="Search client, lender, owner…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="gpj-register-search" />
        </div>
        <div className="gpj-reg-wrap">
          <table className="gpj-reg" data-testid="gpj-register">
            <thead><tr><th>Status</th><th>File</th><th>Stage</th><th>Lender</th><th>Owner</th><th>In stage</th><th>Settles</th><th className="r">Loan</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="gpj-empty">No files match.</td></tr>}
              {rows.map((f) => (
                <tr key={f.id} onClick={() => onOpenFile?.({ id: f.id })} data-testid={`gpj-reg-row-${f.id}`}>
                  <td><span className={`gpj-badge gpj-b-${f.status}`}>{LABEL[f.status]}</span></td>
                  <td><strong>{f.client}</strong><small>{f.purpose || '—'}</small></td>
                  <td>{f.stage_label}</td>
                  <td>{f.lender || '–'}</td>
                  <td>{f.owner || '–'}</td>
                  <td className={`num ${f.ph_status === 'over' ? 'red' : f.ph_status === 'near' ? 'amber' : ''}`}>{f.stage_limit != null ? `${f.bd_in_stage} of ${f.stage_limit} days` : `${f.bd_in_stage} day${f.bd_in_stage === 1 ? '' : 's'}`}</td>
                  <td>{fmtDate(f.settle_date)}</td>
                  <td className="r">{dollars(f.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
